
import { defineComponent, PropType } from 'vue'
import { mapActions } from 'vuex'

import TeamDetailMixin from '@/mixins/teamDetailMixin'
import ActiveUserMixin from '~/mixins/activeUserMixin'

import { getDefaultAvatar } from '~/utils/ui'

import { TeamActions } from '~/store/team/detail'
import { TeamPerms } from '~/types/permTypes'
import { TeamMember } from '~/types/types'

export default defineComponent({
  name: 'TransferOwnershipModal',
  mixins: [TeamDetailMixin, ActiveUserMixin],
  data() {
    return {
      validEmail: true,
      emailEmpty: false,
      searchCandidate: '',
      transferInProgress: false,
      showSearchResults: false,
      newOwner: null as TeamMember | null,
      updateBillingEmail: false,
      oldOwnerTypedEmail: '',
      listLoading: false,
      searchResults: [] as Array<TeamMember> | undefined,
      itemInFocus: -1
    }
  },
  props: {
    showModal: Boolean,
    members: Array as PropType<TeamMember[]>,
    transferLoading: {
      type: Boolean,
      default: false
    },
    transferSuccess: {
      type: Boolean,
      default: false
    }
  },
  mounted() {
    this.fetchTransferCandidates()
  },
  computed: {
    transferOwnershipCandidates(): TeamMember[] {
      if (!this.members) return []
      return this.members.filter((member) => member.isPrimaryUser === false)
    },
    canInviteMembers(): boolean {
      return this.$gateKeeper.team(TeamPerms.MANAGE_TEAM_MEMBERS, this.teamPerms.permission)
    }
  },
  methods: {
    getDefaultAvatar,
    ...mapActions('team/detail', {
      queryTeamMembers: TeamActions.QUERY_TEAM_MEMBERS
    }),
    async fetchTransferCandidates(refetch = false): Promise<void> {
      this.listLoading = true
      const { owner, provider } = this.$route.params
      this.queryTeamMembers({
        login: owner,
        provider,
        currentPage: 0,
        limit: 10,
        query: this.searchCandidate,
        canInvite: this.canInviteMembers,
        refetch
      }).then((teamMembers) => {
        this.searchResults = teamMembers?.filter(
          (member: TeamMember) => member.isPrimaryUser === false
        )
      })

      this.listLoading = false
    },
    setNewOwner(member: TeamMember) {
      this.newOwner = member
      this.hideResults()
      this.searchCandidate = ''
    },
    clearNewOwner() {
      this.newOwner = null
      this.clearItemInFocus()
      this.fetchTransferCandidates()
    },
    close(): void {
      this.$emit('close')
    },
    hideResults(): void {
      this.showSearchResults = false
    },
    revealResults(): void {
      this.showSearchResults = true
    },
    handleKeyUp(event: KeyboardEvent): void {
      this.revealResults()
      if (event.key === 'ArrowDown') this.focusNext()
    },
    handleSearchResultKeyDown(event: KeyboardEvent, member: TeamMember): void {
      if (event.key === 'Enter') {
        this.setNewOwner(member)
        this.clearItemInFocus()
      } else if (event.key === 'ArrowDown') this.focusNext()
      else if (event.key === 'ArrowUp') this.focusPrevious()
    },
    // Sets the index of the currently focused item
    setItemInFocus(index: number): void {
      this.itemInFocus = index
    },
    // Resets the focused item index
    clearItemInFocus(): void {
      this.itemInFocus = -1
    },
    // Moves focus the next item in the results list
    focusNext(): void {
      if (this.searchResults && this.itemInFocus < this.searchResults.length - 1) {
        const nextItem = this.$refs[`searchResult${this.itemInFocus + 1}`] as Array<HTMLElement>
        if (nextItem[0]) nextItem[0].focus()
      }
    },
    // Moves focus the previous item in the results list
    focusPrevious(): void {
      if (this.searchResults && this.itemInFocus > 0) {
        const prevItem = this.$refs[`searchResult${this.itemInFocus - 1}`] as Array<HTMLElement>
        if (prevItem[0]) prevItem[0].focus()
      } else if (this.itemInFocus === 0) {
        const input = this.$refs.input as HTMLElement
        if (input) input.focus()
      }
    },
    // The timeout allows a search result item to be clicked before the results are hidden
    // The if condition ensures that the results window is not hidden in case a result item is focused via keyboard navigation,
    // allowing subsquent keydown events to continue to navigate the results list
    handleBlur() {
      setTimeout(() => {
        if (this.itemInFocus === -1) this.hideResults()
      }, 150)
    },

    validateEmail(el: HTMLInputElement): void {
      this.validEmail = el.checkValidity()
      if (!this.oldOwnerTypedEmail) {
        this.emailEmpty = true
        return
      }
      this.emailEmpty = false
    },

    triggerTransferOwnership(): void {
      if (!this.newOwner) {
        this.$toast.danger('Please choose a new owner for this team.')
        return
      }

      if (this.emailEmpty || !this.validEmail) {
        this.$toast.danger('Please enter a valid email.')
        return
      }

      if (this.oldOwnerTypedEmail !== this.viewer.email) {
        this.$toast.danger('Please ensure that your email address is entered correctly.')
        return
      }
      this.$emit('primaryAction', this.newOwner.user.id, this.updateBillingEmail)
    }
  }
})
